import { push } from 'redux-first-history';
import { all, takeEvery, takeLatest, put, call, select } from 'redux-saga/effects';
import _uniq from 'lodash/uniq';
import actions from './actions';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import { keyByProperty } from '../../../helpers/core';
import notifications from '../../../redux/notifications/actions';
import Api from '../../../helpers/api';
import { t } from '../../../i18n';

const agencyApi = new Api('agencies');
const agencyContractApi = new Api('agency_contracts/:id');
const inviteApi = new Api('agencies/invites/:id');
const agencyContractInviteApi = new Api('agency_contracts/invites/:id');
const agencyFormDataApi = new Api('agencies/form_data');
const agentsListApi = new Api('agencies/:id/agents/list');
const agencyLegalEntitiesApi = new Api('legal_entities/list');

// Reshapes new contract show response (flag ON) into the legacy agency shape
// that formatAgencyInvite/editForm consumers expect.
function mapContractToAgencyInvite(contract) {
  return {
    ...contract.agency,
    invite: {
      id: contract.invite?.id,
      markup_percentage: contract.invite?.markup_percentage ?? null,
      status: contract.invite?.status,
      sent_at: contract.invite?.sent_at,
      activated_at: contract.invite?.activated_at,
    },
    districts: contract.districts || [],
    legal_entity: contract.legal_entity || null,
  };
}

function formatAgencyInvite(agency) {
  if (agency?.contact) {
    agency.first_name = agency.contact.first_name;
    agency.last_name = agency.contact.last_name;
    agency.email = agency.contact.email;
    delete agency.contact;
  }
  agency.id = agency.invite.id;
  agency.markup_percentage = agency.invite.markup_percentage;
  delete agency.invite;

  agency.legal_entity_id = agency.legal_entity?.id || '';

  if (!agency.states_ids) {
    agency.states_ids = [];
  }

  if (!agency.districts) {
    agency.districts = [];
  }

  agency.districts.forEach(district => {
    district.positions.forEach(position => {
      position.enabled = true;
    });
    agency.states_ids.push(district.state);
    agency.states_ids = agency.states_ids.sort((a, b) => a.name.localeCompare(b.name));
    agency.states_ids = agency.states_ids.map(state => state.id).filter(id => id);
    agency.states_ids = _uniq(agency.states_ids);
  });

  const markupValue = parseFloat(agency.markup_percentage);
  agency.markup_percentage = Number.isFinite(markupValue) ? markupValue : null;
}

export function formatAgencyBeforeCreate(editForm, isNewAgencyCreation, chosenAgency, chosenAgent) {
  const propsArray = Object.values(editForm);

  const agency = keyByProperty(propsArray, 'name', prop => prop.value);

  const contact = {
    id: chosenAgent?.id || null,
    first_name: agency.first_name,
    last_name: agency.last_name,
    email: chosenAgent?.email || agency.email,
  };

  delete agency.first_name;
  delete agency.last_name;
  delete agency.email;

  agency.name = isNewAgencyCreation ? editForm.name.value : chosenAgency.name;
  agency.id = chosenAgency?.id || null;

  return { ...agency, contact };
}

function* loadData({ payload }) {
  try {
    const { id, cb } = payload;

    let data = null;
    let result = null;

    const baseRequests = {
      formInfo: call(sendRequest, agencyFormDataApi.load, {}, notifications.REQUEST_ERROR),
    };

    if (id) {
      const isLegalEntityEnabled = yield select(state =>
        Boolean(state.StartAppSettings?.feature_flags?.legal_entity_enabled),
      );
      const showApi = isLegalEntityEnabled ? agencyContractApi : agencyApi;

      result = yield all({
        agency: call(sendRequest, showApi.load, { id }, notifications.REQUEST_ERROR),
        ...baseRequests,
      });

      if (isLegalEntityEnabled) {
        const contract = result.agency?.data?.success?.data;
        data = contract ? mapContractToAgencyInvite(contract) : null;
      } else {
        data = result.agency?.data?.success?.data?.agency;
      }

      if (data) {
        formatAgencyInvite(data);
      }
    } else {
      result = yield all(baseRequests);
    }

    const formInfo = result?.formInfo?.data?.success?.data;

    yield put(actions.loadDataSuccess({ data, formInfo }));

    if (cb) {
      cb();
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadAgentsList({ payload }) {
  try {
    const { id } = payload;

    const result = yield all({
      agency: call(sendRequest, agentsListApi.load, { id }, notifications.REQUEST_ERROR),
    });

    const records = result.agency?.data?.success?.data?.data;

    yield put(actions.loadAgentsListSuccess({ agentsList: records }));
  } catch (error) {
    logger.error(error);
  }
}

function* loadAgencyLegalEntities({ payload }) {
  try {
    // Existing-agency flow passes `id` → filter by that agency. New-agency flow passes nothing
    // → BE returns the full LE list (no contracts exist yet for a soon-to-be-created agency).
    const search = payload?.id ? { agency_id: payload.id } : {};
    const result = yield call(sendRequest, agencyLegalEntitiesApi.load, { search }, notifications.REQUEST_ERROR);
    const records = result?.data?.success?.data?.records ?? [];

    yield put(actions.loadAgencyLegalEntitiesSuccess({ agencyLegalEntities: records }));
  } catch (error) {
    logger.error(error);
    yield put(actions.loadAgencyLegalEntitiesError());
  }
}

function* createAgency({ payload }) {
  try {
    const { editForm, isNewAgencyCreation, chosenAgency, chosenAgent, cb } = payload;
    const agency = formatAgencyBeforeCreate(editForm, isNewAgencyCreation, chosenAgency, chosenAgent);
    agency.districts = editForm.districts;
    agency.districts.forEach(district => {
      district.original_district_id = district.id;
      district.positions = district.positions.filter(position => position.enabled);
    });

    const result = yield call(sendRequest, agencyApi.createItem, { data: { agency } }, notifications.REQUEST_ERROR);

    if (result?.data?.agency) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.created_successfully', {
            item: t('dash.agencies.invite.new_invite_notification'),
          }),
        }),
      );
      yield put(actions.saveSuccess(result.data.agency));

      if (cb) {
        cb();
      }
    } else {
      if (result) {
        yield put(actions.saveError({ data: result }));
      }

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.check_validation_errors'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateAgency({ payload }) {
  try {
    const propsArray = Object.values(payload.editForm);
    const agency = keyByProperty(propsArray, 'name', prop => prop.value);
    agency.districts = payload.editForm.districts;

    // LE is immutable on agency invite edit (BE §5c) — strip from PUT payload.
    // legal_entity object is a load-time helper only, not part of update.
    delete agency.legal_entity_id;
    delete agency.legal_entity;

    const isLegalEntityEnabled = yield select(state =>
      Boolean(state.StartAppSettings?.feature_flags?.legal_entity_enabled),
    );
    const updateApi = isLegalEntityEnabled ? agencyContractInviteApi : inviteApi;

    const result = yield call(
      sendRequest,
      updateApi.updateItem,
      { data: { agency_invite: agency }, id: agency.id },
      notifications.REQUEST_ERROR,
    );

    if (result?.data?.agency_invite) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.agencies.invite.new_invite_notification'),
          }),
        }),
      );
      yield put(actions.saveSuccess(result.data.agency_invite));

      const redirectId = isLegalEntityEnabled
        ? result.data.agency_invite.agency_contract_id
        : result.data.agency_invite.agency.id;

      yield put(push(`/agencies/invite/${redirectId}?&tab[]=details`));
    } else {
      if (result) {
        yield put(actions.saveError({ data: result }));
      }

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.check_validation_errors'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD_REQUEST, loadData),
    takeEvery(actions.CREATE_REQUEST, createAgency),
    takeEvery(actions.UPDATE_REQUEST, updateAgency),
    takeEvery(actions.LOAD_AGENTS_LIST_REQUEST, loadAgentsList),
    // takeLatest — if the user quickly switches agencies, only the most recent LE fetch survives.
    takeLatest(actions.LOAD_AGENCY_LEGAL_ENTITIES_REQUEST, loadAgencyLegalEntities),
  ]);
}
